import { clamp, equals, l2Distance, rectOffset } from "@/utils/math"
import { ImageInfoRecord } from "@/lib/catalog"
import { AI } from "@/lib/ai/ai"
import equal from "fast-deep-equal"
import {
	absoluteDifference,
	extractKeys,
	getAbsDiffForDifferentRects,
	getAbsDiffForImagePatch,
	getAbsDiffForRectBounds,
	getAllFaceBounds,
	getFaceArea,
	getMean,
	getRectBoundaryIndices,
	getRectCenter,
	isCropEqual,
	isRectAtEdge,
	isRectCoveredByCrop,
	meanAbsoluteDifference,
	percentDifference,
	rectOverlapRatio,
} from "./ai-calibration-utils"
import { constrainCrop } from "@/components/Crop/crop-interaction-handlers"
import { SettingsState } from "@/store/settings-state"
import {
	DefaultExcludedAdjustmentKeys,
	getAdjustmentRange,
	getDefaultAdjustments,
} from "@/core/adjustments"
import { Adjustments, Crop, HealingItem } from "@/core/types"
import { transferLocalAdjustments } from "./ai-calibration-mask"
import { logAiInfo } from "@/lib/ai/utils"
import {
	transformInUncroppedSpace,
	transformHealingMask,
	rectToInitialOrientation,
	getBriefFeature,
	isMatched,
	rectsToInitialOrientationAndShrink,
} from "@/lib/ai/alignment/ai-image-align"
import { vec2 } from "gl-matrix"
import {
	ImageFeature,
	getDeepClusterDistance,
	imageRatioDifference,
	lightKeys,
	wbKeys,
} from "./ai-calibration-feature"
import { isObjectMask } from "@/core/utils/masks"
import { FlowResult } from "@/lib/ai/detection/optical-flow"
import { fetchFlowFeature, storeFlowFeature } from "@/storage/indexeddb"
import { getFileForPhoto } from "@/store/files-state"
import { getPhotoMetadata } from "@/store/photo/photos-state"
import { getImageInfo } from "@/store/processing/image-info"

export const sceneDThreshBW = 0.12
export const sceneDThreshForVerySimilarNeighbor = 0.09 // very similar photos should share same useOwnAutoLight flag for a given key photo

// scene distance thresholds for transferring healing/crop/straighten/perspective
// thresholds for mask transfer is defined in ai-calibration-mask.ts
export const sceneDThreshForAlign = 0.14
const sceneDThreshForCrop = sceneDThreshForAlign
const sceneDThreshForHealing = sceneDThreshForAlign
const sceneDThreshForTransformLarge = 0.1
const sceneDThreshForTransformSmall = 0.01

const perspectiveKeys = [
	"perspectiveVertical",
	"perspectiveHorizontal",
	"perspectiveRotate",
	"perspectiveScale",
	"perspectiveAspect",
	"perspectiveOffsetX",
	"perspectiveOffsetY",
] as const

export interface KeyAdjmtInfo {
	imageInfo: ImageInfoRecord
	keyImageInfo: ImageInfoRecord
	keyImageCurrentAdjustments: Adjustments
	settings: SettingsState
}

export interface TransferInfo {
	imageInfo: ImageInfoRecord
	keyImageInfo: ImageInfoRecord
	keyImageCurrentAdjustments: Adjustments
	dScene: number
	dImageAspect: number
}

interface FlowResultInfo {
	flowResult: FlowResult | null | undefined
	dScene: number
	dImageAspect: number
}

/**
 * Determine if we should use own auto light params for the image.
 * useOwnAutoLight=true: use half color match on top of key photo adjustments (new algorithm) or own auto light params (old algorithm)
 * useOwnAutoLight=false: use key photo's full adjustments, only adapt exposure
 */
export function shouldUseOwnAutoLight(options: {
	imageInfo: ImageInfoRecord
	keyImageInfo: ImageInfoRecord
	dScene?: number
}) {
	const { imageInfo, keyImageInfo } = options
	const dScene = options.dScene ?? getDeepClusterDistance(imageInfo, keyImageInfo)

	// If two photos' scene or lighting is quite different, use own auto light params
	const lightFeat = imageInfo.features.light
	const keyLightFeat = keyImageInfo.features.light
	let useOwnAutoLight = false
	if (dScene > 0.185) {
		// scene is too different
		useOwnAutoLight = true
	} else if (dScene > sceneDThreshForVerySimilarNeighbor) {
		// scene is similar but not identical, check if lighting is quite different
		const overallLightDist = meanAbsoluteDifference(
			lightFeat.percentiles,
			keyLightFeat.percentiles,
		)
		const faceLightDist = meanAbsoluteDifference(
			lightFeat.facePercentiles,
			keyLightFeat.facePercentiles,
			0.4,
		)
		const faceWbDist = l2Distance(
			imageInfo.features.wb.face,
			keyImageInfo.features.wb.face,
			0.4,
		)
		const lightDist = overallLightDist + faceLightDist
		if (
			overallLightDist > 0.08 ||
			(lightDist > 0.08 && overallLightDist > 0.035) ||
			faceLightDist > 0.035 ||
			faceWbDist > 0.035 ||
			faceLightDist + faceWbDist > 0.05
		)
			useOwnAutoLight = true

		logAiInfo(`useOwnAutoLight for face_count=${lightFeat.faceCount}`, {
			lightDist,
			overallLightDist,
			faceLightDist,
			useOwnAutoLight,
			faceWbDist,
		})
	}

	return useOwnAutoLight
}

function getAdaptedExposure(
	imageInfo: ImageInfoRecord,
	keyImageInfo: ImageInfoRecord,
	keyExposure: number,
) {
	const lightFeat = imageInfo.features.light
	const keyLightFeat = keyImageInfo.features.light

	let delta = lightFeat.avgLum - keyLightFeat.avgLum
	logAiInfo("adapt exposure", { overallDelta: delta })
	// Also combine face lum delta assuming faces are roughly the same
	if (
		lightFeat.faceCount > 0 &&
		keyLightFeat.faceCount > 0 &&
		Math.abs(lightFeat.faceCount - keyLightFeat.faceCount) <= 1
	) {
		const faceDelta = lightFeat.faceLum - keyLightFeat.faceLum
		const facePercentDiff = percentDifference(lightFeat.facePercent, keyLightFeat.facePercent)
		let faceScale = 1.5 * Math.max(0, 0.5 - facePercentDiff)
		if (lightFeat.faceCount === 1) {
			faceScale = Math.min(0.75, faceScale + 0.5)
		} else if (lightFeat.faceCount === 2) {
			faceScale = Math.min(0.7, faceScale + 0.35)
		} else {
			faceScale = Math.min(0.6, faceScale + 0.2)
		}
		if (lightFeat.facePercent < 0.004) {
			faceScale *= Math.max(0, lightFeat.facePercent - 0.001) / 0.003
		}
		delta = delta * (1 - faceScale) + faceDelta * faceScale
		logAiInfo("adapt exposure with face", { delta, faceDelta, facePercentDiff, faceScale })
	}

	return clamp(keyExposure - delta * 3, ...getAdjustmentRange("exposure"))
}

function isAdjustmentsGlobal(adjustments: Partial<Adjustments> | null | undefined) {
	if (!adjustments) return false

	// distinguish between full aiStyleAdjustments and keyAdjustments (in previous versions we only store keyAdjustments)
	if (Object.keys(adjustments).length < 12) return false

	const hasHealing = adjustments.healingMasks && adjustments.healingMasks.length
	return !hasHealing && !hasMasksWithCoords(adjustments)
}

export function getGlobalColorAdjustments(currentAdjustments: Partial<Adjustments>) {
	const globalColorAdjustments = structuredClone(currentAdjustments)
	delete globalColorAdjustments.localAdjustments
	DefaultExcludedAdjustmentKeys.forEach((key) => {
		delete globalColorAdjustments[key]
	})
	return globalColorAdjustments
}

export function getAdjustmentsFromVerySimilarNeighbor(imageInfo: ImageInfoRecord) {
	const verySimilarNeighborId = imageInfo.features.verySimilarNeighbor
	const neighbor = verySimilarNeighborId ? getImageInfo(verySimilarNeighborId) : null

	// if very similar neighbor is already processed, use its adjustments if it doesn't include localized adjustments
	if (
		neighbor &&
		neighbor.imageId !== imageInfo.keyNeighborId &&
		neighbor.keyNeighborId === imageInfo.keyNeighborId &&
		neighbor.needApplyStyle == false &&
		neighbor.aiStyleAdjustments &&
		isAdjustmentsGlobal(neighbor.aiStyleAdjustments)
	) {
		const result = structuredClone(neighbor.aiStyleAdjustments)
		DefaultExcludedAdjustmentKeys.forEach((key) => {
			delete result[key]
		})
		const neighborExposure = neighbor.aiStyleAdjustments.exposure
		if (neighborExposure != undefined) {
			result.exposure = getAdaptedExposure(imageInfo, neighbor, neighborExposure)
		}
		return result
	}

	return null
}

function transferLightAndWB(info: TransferInfo) {
	const { imageInfo, keyImageInfo, keyImageCurrentAdjustments, dScene } = info

	logAiInfo(`dScene of ${imageInfo.imageId} vs key ${keyImageInfo.imageId}:`, dScene)

	let useOwnAutoLight = imageInfo.features.useOwnAutoLightMap?.[keyImageInfo.imageId]
	if (useOwnAutoLight == undefined) {
		useOwnAutoLight = shouldUseOwnAutoLight({ imageInfo, keyImageInfo, dScene })
		imageInfo.features.useOwnAutoLightMap = {
			...imageInfo.features.useOwnAutoLightMap,
			[keyImageInfo.imageId]: useOwnAutoLight,
		}
	}
	logAiInfo(`useOwnAutoLight=${useOwnAutoLight}`)
	const keys = !useOwnAutoLight ? [...wbKeys, ...lightKeys] : wbKeys

	const keyAdjustments = extractKeys(keyImageCurrentAdjustments, keys)

	// useOwnAutoLight=false, image's original exposure could be different than the keyNeighbor, compensate for it
	if (!useOwnAutoLight) {
		const keyExposure = keyImageCurrentAdjustments.exposure
		keyAdjustments["exposure"] = getAdaptedExposure(imageInfo, keyImageInfo, keyExposure)
	}

	keyAdjustments.temperature = keyAdjustments.temperature ?? keyImageInfo.features.meta.init_temp
	keyAdjustments.tint = keyAdjustments.tint ?? keyImageInfo.features.meta.init_tint

	return keyAdjustments
}

export function getKeyAdjustmentsFast(keyAdjmtInfo: KeyAdjmtInfo): Partial<Adjustments> {
	// Skip compute alignment matrix result for fast speed

	const { imageInfo, keyImageInfo } = keyAdjmtInfo
	const dScene = getDeepClusterDistance(imageInfo, keyImageInfo)
	const dImageAspect = imageRatioDifference(imageInfo.features, keyImageInfo.features)

	let flowResult: FlowResult | undefined | null = undefined

	if (dImageAspect >= 0.001 || dScene >= sceneDThreshForAlign) flowResult = null

	const flowResInfo: FlowResultInfo = { flowResult, dScene, dImageAspect }

	return getKeyAdjustmentsWithAlignResult(keyAdjmtInfo, flowResInfo)
}

export async function getKeyAdjustments(keyAdjmtInfo: KeyAdjmtInfo): Promise<Partial<Adjustments>> {
	// Compute flow result if needed
	const flowResInfo = await getFlowResult(keyAdjmtInfo)

	return getKeyAdjustmentsWithAlignResult(keyAdjmtInfo, flowResInfo)
}

function getKeyAdjustmentsWithAlignResult(
	keyAdjmtInfo: KeyAdjmtInfo,
	flowResInfo: FlowResultInfo,
): Partial<Adjustments> {
	const { imageInfo, keyImageInfo, keyImageCurrentAdjustments, settings } = keyAdjmtInfo
	const {
		adaptiveCrop,
		adaptiveStraighten,
		adaptivePerspective,
		adaptiveMasking,
		adaptiveHealing,
		autoStraighten,
		autoPerspective,
	} = settings

	const { flowResult, dScene, dImageAspect } = flowResInfo

	const transferInfo: TransferInfo = {
		imageInfo,
		keyImageInfo,
		keyImageCurrentAdjustments,
		dScene,
		dImageAspect,
	}

	let keyAdjustments = transferLightAndWB(transferInfo) as Partial<Adjustments>

	// transfer B&W color in keyAdjustments because bias.saturation is now -100 and no longer correct
	if (dScene < sceneDThreshBW) {
		keyAdjustments["saturation"] = keyImageCurrentAdjustments["saturation"]
	}

	// transfer crop from keyImage
	if (adaptiveCrop) {
		const cropAdjustments = transferCrop(transferInfo, flowResult)
		if (cropAdjustments) keyAdjustments = { ...keyAdjustments, ...cropAdjustments }
	}

	// transfer perspective transform including auto-straighten from keyImage
	if (adaptivePerspective) {
		const transformAdjustments = transferPerspective(transferInfo, autoPerspective)
		if (transformAdjustments) keyAdjustments = { ...keyAdjustments, ...transformAdjustments }
	}

	// transfer cropStraighten from keyImage
	if (adaptiveStraighten) {
		const cropStraighten = transferStraighten(transferInfo, autoStraighten)
		if (cropStraighten != null) keyAdjustments["cropStraighten"] = cropStraighten
	}

	// Constrain crop
	keyAdjustments = constrainCropInKeyAdjustments(
		imageInfo,
		keyAdjustments,
		autoStraighten,
		autoPerspective,
	)

	// Transfer local adjustments (masks) only when useOwnAutoLight == false
	if (adaptiveMasking && keyAdjustments["exposure"] != undefined) {
		const localAdjustments = transferLocalAdjustments(transferInfo, keyAdjustments, flowResult)
		if (localAdjustments && localAdjustments.length > 0) {
			keyAdjustments["localAdjustments"] = localAdjustments
		} else if (keyImageInfo.biases?.localAdjustmentsManual?.length) {
			// keyPhoto has local, but not transferred successfully, use own auto + bias for light
			lightKeys.forEach((key) => {
				delete keyAdjustments[key]
			})
		}
		logAiInfo("transferred localAdjustments:", localAdjustments)
	}

	// Transfer healing
	if (adaptiveHealing) {
		const healing = transferHealing(transferInfo, flowResult)
		if (healing != null) keyAdjustments["healingMasks"] = healing
	}

	logAiInfo("getKeyAdjustments:", { imageInfo, keyImageInfo, dScene, keyAdjustments })

	return keyAdjustments
}

async function getFlowResult(keyAdjmtInfo: KeyAdjmtInfo): Promise<FlowResultInfo> {
	const { imageInfo, keyImageInfo, keyImageCurrentAdjustments, settings } = keyAdjmtInfo
	const autoStraighten = settings.autoStraighten

	const dScene = getDeepClusterDistance(imageInfo, keyImageInfo)
	const dImageAspect = imageRatioDifference(imageInfo.features, keyImageInfo.features)

	// Compute alignment info (optical flow result) if needed
	const needAlignment = hasLocalizedChanges(
		keyImageCurrentAdjustments,
		keyImageInfo,
		autoStraighten,
	)

	let flowResult: FlowResult | undefined | null = undefined

	if (flowResult === undefined && needAlignment) {
		if (
			dImageAspect < 0.001 && // only align for images with the same aspect ratio
			dScene < sceneDThreshForAlign
		) {
			// Fetch key photo's flow feature from indexeddb
			const keyFlowFeature = await fetchFlowFeature(keyImageInfo.imageId)
			if (keyFlowFeature) {
				flowResult = await AI.computeOpticalFlowResult(keyFlowFeature, imageInfo)
			} else {
				// If indexdb record is missing, re-extract flow feature for key photo
				// bad performance as loading photos twice, but the case should be quite rare
				const keyId = keyImageInfo.imageId
				console.warn(
					"indexeddb record not found: recomputing flow feature for key photo",
					keyId,
				)

				// Load key photo
				let file = getFileForPhoto(keyId)
				let metadata = getPhotoMetadata(keyId)
				if (!file) {
					throw "Cannot find file specified"
				}

				await AI.loadImage(file, { metadata })

				// Extract and store flow feature
				const keyFlowFeature = await AI.extractOpticalFlowFeature(keyImageInfo)
				await storeFlowFeature(keyFlowFeature)

				// Reload previous loaded photo and compute flowResult
				file = getFileForPhoto(imageInfo.imageId)
				metadata = getPhotoMetadata(keyId)
				if (!file) {
					throw "Cannot find file specified"
				}
				await AI.loadImage(file, { metadata })

				flowResult = await AI.computeOpticalFlowResult(keyFlowFeature, imageInfo)
			}
		} else {
			flowResult = null
		}

		// console.log("flow computed for", {
		// 	keyId: keyImageInfo.imageId,
		// 	imageId: imageInfo.imageId,
		// 	flowResult,
		// })
	}

	return { flowResult, dScene, dImageAspect }
}

function isCropAdjusted(adjustments: Adjustments, imageInfo: ImageInfoRecord) {
	const crop = adjustments.crop

	// no manual crop adjustment cases:
	if (!crop || equal(crop, [0, 0, 1, 1])) return false

	const constrainedCrop = constrainCrop(
		{ ...adjustments, crop: [0, 0, 1, 1] },
		imageInfo.features.imageSize,
		imageInfo.features.initialOrientation ?? 0,
	)
	if (isCropEqual(crop, constrainedCrop)) return false

	return true
}

function isStraightenAdjusted(
	adjustments: Adjustments,
	imageInfo: ImageInfoRecord,
	autoStraightenSetting: boolean,
) {
	const currentStraighten = adjustments.cropStraighten

	if (autoStraightenSetting) {
		const autoStraightenVal = imageInfo.features.autoCorrection.cropStraighten ?? 0
		if (currentStraighten === autoStraightenVal) return false
	} else {
		if (currentStraighten === 0) return false
	}

	return true
}

function hasMasksWithCoords(adjustments: Partial<Adjustments>) {
	const localAdjustments = adjustments.localAdjustments
	if (!localAdjustments) return false

	for (const maskGroup of localAdjustments) {
		for (const mask of maskGroup.masks) {
			if (!isObjectMask(mask)) {
				return true
			}
		}
	}

	return false
}

export function hasLocalizedChanges(
	adjustments: Adjustments,
	imageInfo: ImageInfoRecord,
	autoStraightenSetting: boolean,
) {
	const hasHealing = adjustments.healingMasks && adjustments.healingMasks.length

	return (
		isCropAdjusted(adjustments, imageInfo) ||
		isStraightenAdjusted(adjustments, imageInfo, autoStraightenSetting) ||
		hasHealing ||
		hasMasksWithCoords(adjustments)
	)
}

function constrainCropInKeyAdjustments(
	imageInfo: ImageInfoRecord,
	keyAdjustments: Partial<Adjustments>,
	autoStraighten: boolean,
	autoPerspective: boolean,
) {
	const defaultAdjustments = getDefaultAdjustments()

	const aiAdjustments = imageInfo.presetId
		? imageInfo.aiAdjustmentsMap[imageInfo.presetId]
		: undefined
	const defaultStraighten = autoStraighten
		? (imageInfo.features.autoCorrection.cropStraighten ?? 0)
		: 0
	const defaultStraightenReal = aiAdjustments ? aiAdjustments["cropStraighten"] : undefined
	const cropStraighten =
		keyAdjustments["cropStraighten"] ?? defaultStraightenReal ?? defaultStraighten

	const defaultVertical = autoPerspective
		? (imageInfo.features.autoCorrection.perspectiveVertical ?? 0)
		: 0
	const defaultVerticalReal = aiAdjustments ? aiAdjustments["perspectiveVertical"] : undefined
	const perspectiveVertical =
		keyAdjustments["perspectiveVertical"] ?? defaultVerticalReal ?? defaultVertical
	// logAiInfo("constrain crop:", { defaultStraighten, defaultVertical, autoPerspective, settings })
	const crop = constrainCrop(
		{ ...defaultAdjustments, ...keyAdjustments, cropStraighten, perspectiveVertical },
		imageInfo.features.imageSize,
		imageInfo.features.initialOrientation ?? 0,
	)
	keyAdjustments["crop"] = crop
	keyAdjustments["cropStraighten"] = cropStraighten

	return keyAdjustments
}

function transferHealing(info: TransferInfo, flowResult: FlowResult | null | undefined) {
	const { keyImageCurrentAdjustments, dScene, dImageAspect } = info

	const nonAIHealingMasks = keyImageCurrentAdjustments.healingMasks?.filter((healingItem) => {
		return !healingItem.aiVariations
	})

	if (nonAIHealingMasks && nonAIHealingMasks?.length) {
		if (dScene < sceneDThreshForHealing && dImageAspect < 0.001) {
			if (flowResult === undefined) {
				// for getKeyAdjustmentsFast
				return structuredClone(keyImageCurrentAdjustments.healingMasks)
			}

			const transformed = nonAIHealingMasks.map((healingItem) =>
				transferHealingItem(healingItem, info, flowResult),
			)

			const result = redetectHealingSource(transformed, info, flowResult).filter(
				(val): val is NonNullable<typeof val> => val != null,
			)

			logAiInfo("original healing:", keyImageCurrentAdjustments["healingMasks"])
			logAiInfo("transferred healing:", result)

			return result
		}
	}

	return null
}

function transferHealingItem(
	healingItem: HealingItem,
	info: TransferInfo,
	flowResult: FlowResult | null,
) {
	const { imageInfo, dScene } = info
	const keyRect = rectToInitialOrientation(
		healingItem.destinationRect,
		imageInfo.features.initialOrientation ?? 0,
	)

	const gray = flowResult?.grayImage
	const keyGray = flowResult?.keyGrayImage

	if (!gray || !keyGray) return null

	let aveDiff: number | undefined = undefined
	let stdDiff: number | undefined = undefined

	// First decide whehter the healing region is a sensor dust spot
	if (keyRect.width * keyRect.height < 0.006) {
		// If healing region is small, and healing image patch almost identical in both images (or differ by a constant due to lighting),
		//  we think it's dust spot, because dust is attached to lens and doesn't move with camera view point.

		const res = getAbsDiffForImagePatch(gray, keyGray, keyRect)
		aveDiff = res.aveDiff
		stdDiff = res.stdDiff

		logAiInfo("dust healing:", { keyRect, aveDiff, stdDiff })

		if (aveDiff < 1.5 || stdDiff < 1 || (stdDiff < 2 && aveDiff < 10)) {
			// dust spot, no need to transform coordinates
			return structuredClone(healingItem)
		}
	}

	// still possible to be dust spot, compare which of the transformed or non-transformed dest patch is closer to key's patch

	const transformedHealingItem = flowResult
		? transformHealingMask(healingItem, {
				flowResult,
				orientedSize: imageInfo.features.imageSize,
				initialOrientation: imageInfo.features.initialOrientation ?? 0,
				dScene,
			})
		: null

	if (!transformedHealingItem) {
		if ((stdDiff || 100) < 5) return structuredClone(healingItem)
		else return null
	}

	const transRect = rectToInitialOrientation(
		transformedHealingItem.destinationRect,
		imageInfo.features.initialOrientation ?? 0,
	)

	const {
		aveDiff: aveDiffTrans,
		stdDiff: stdDiffTrans,
		histDiff,
	} = getAbsDiffForDifferentRects(gray, keyGray, transRect, keyRect)

	// Threshold to determine if dest region is similar to ref's dest region;
	// if too different, the to-be-erased object might be gone, so do not transfer healing
	const totalDiffThresh = 25
	const aveDiffThresh = 18
	const stdDiffThresh = 8

	const diffTotalTrans = aveDiffTrans * 0.7 + stdDiffTrans * 0.7

	logAiInfo("dust healing2:", {
		id: imageInfo.imageId,
		transRect,
		aveDiffTrans,
		stdDiffTrans,
		diffTotalTrans,
		histDiff,
	})

	// dust spot has lower diff
	if (aveDiff != undefined && stdDiff != undefined) {
		const diffTotal = aveDiff * 0.7 + stdDiff * 0.7
		if (
			stdDiff < stdDiffTrans &&
			(diffTotal < totalDiffThresh * 0.8 || stdDiff < stdDiffThresh * 0.8) &&
			(stdDiff < 5 || diffTotal < 0.5 * diffTotalTrans)
		)
			return structuredClone(healingItem)
	}

	// If rect clamped at edge, the diff computation is not valid, just return it or handle it in the future
	if (
		diffTotalTrans < totalDiffThresh ||
		stdDiffTrans < stdDiffThresh || // if only lighting change, std diff should be small
		(isRectAtEdge(transformedHealingItem.destinationRect) &&
			!isRectAtEdge(healingItem.destinationRect))
	)
		return transformedHealingItem

	// Comparing pixel / histogram difference in the above may not be robust, use feature matching:
	const srcFeatures = getBriefFeature(keyGray, keyRect)
	const destFeatures = getBriefFeature(gray, transRect)
	const isFeatureMatched = isMatched(srcFeatures, destFeatures)
	logAiInfo("feature matched?", isFeatureMatched)

	if (isFeatureMatched) return transformedHealingItem

	// If there are no or few features, using relaxed condition
	if (
		(srcFeatures.length < 15 || destFeatures.length < 15) &&
		(aveDiffTrans < aveDiffThresh ||
			histDiff < 0.45 ||
			(histDiff < 0.6 && dScene < 0.04) ||
			(aveDiffTrans < aveDiffThresh + 2 && histDiff < 0.7))
	)
		return transformedHealingItem

	return null
}

function redetectHealingSource(
	healingItems: (HealingItem | null)[],
	info: TransferInfo,
	flowResult: FlowResult | null,
) {
	const gray = flowResult?.grayImage
	const keyGray = flowResult?.keyGrayImage

	if (!gray || !keyGray) return healingItems

	const keyHealingItems = info.keyImageCurrentAdjustments.healingMasks ?? []
	const initialOrientation = info.imageInfo.features.initialOrientation ?? 0

	const boundDiffThresh = 12
	const boundDiffRatioThresh = 1.3

	for (let i = 0; i < healingItems.length; i++) {
		const healingItem = healingItems[i]
		const keyHealingItem = keyHealingItems[i]

		if (!healingItem || !keyHealingItem) continue

		const { rect1: kSrcRect, rect2: kDestRect } = rectsToInitialOrientationAndShrink(
			keyHealingItem.sourceRect,
			keyHealingItem.destinationRect,
			initialOrientation,
		)
		const kSrcIndices = getRectBoundaryIndices(kSrcRect, keyGray.width, keyGray.height)
		const kDestIndices = getRectBoundaryIndices(kDestRect, keyGray.width, keyGray.height)
		if (!kSrcIndices || !kDestIndices) continue
		let keyAveDiff = getAbsDiffForRectBounds(keyGray, kSrcIndices, kDestIndices)
		keyAveDiff = Math.max(keyAveDiff, 1)

		const { rect1: srcRect, rect2: destRect } = rectsToInitialOrientationAndShrink(
			healingItem.sourceRect,
			healingItem.destinationRect,
			initialOrientation,
		)

		const srcIndices = getRectBoundaryIndices(srcRect, gray.width, gray.height)
		const destIndices = getRectBoundaryIndices(destRect, gray.width, gray.height)
		if (!srcIndices || !destIndices) continue

		const aveDiff = getAbsDiffForRectBounds(gray, srcIndices, destIndices)

		logAiInfo("redetectSourceForHealing initial", { srcRect, destRect, aveDiff, keyAveDiff })

		// Transformed source is good enough
		if (aveDiff <= boundDiffThresh || aveDiff / keyAveDiff <= boundDiffRatioThresh) continue

		// First search in small range
		const smRange = 12
		let minDiff = aveDiff
		let minDx = 0
		let minDy = 0
		for (let dx = -smRange; dx <= smRange; dx += 2) {
			for (let dy = -smRange; dy <= smRange; dy += 2) {
				if (dx === 0 && dy === 0) continue

				const overlap = rectOverlapRatio(
					rectOffset(srcRect, dx / gray.width, dy / gray.height),
					destRect,
				)
				if (overlap > 0.1) continue

				const indices = getRectBoundaryIndices(srcRect, gray.width, gray.height, [dx, dy])
				if (indices) {
					const diff = getAbsDiffForRectBounds(gray, indices, destIndices)
					if (diff < minDiff) {
						minDiff = diff
						minDx = dx
						minDy = dy
					}
				}
			}
		}
		logAiInfo("redetectSourceForHealing small-range", { minDx, minDy, minDiff })

		// Need search in larger range
		if (minDiff > boundDiffThresh && minDiff / keyAveDiff > boundDiffRatioThresh) {
			const rw = Math.round(srcRect.width * gray.width)
			const rh = Math.round(srcRect.height * gray.height)
			const lgRangeX = Math.max(rw * 2, 20)
			const stepX = Math.round(lgRangeX / 20)
			const lgRangeY = Math.max(rh * 2, 20)
			const stepY = Math.round(lgRangeY / 20)
			logAiInfo("redetectSourceForHealing large-range", { lgRangeX, lgRangeY, stepX, stepY })

			for (let dx = -lgRangeX; dx <= lgRangeX; dx += stepX) {
				for (let dy = -lgRangeY; dy <= lgRangeY; dy += stepY) {
					if (Math.abs(dx) <= smRange && Math.abs(dy) <= smRange) continue

					const overlap = rectOverlapRatio(
						rectOffset(srcRect, dx / gray.width, dy / gray.height),
						destRect,
					)
					if (overlap > 0.1) continue

					const indices = getRectBoundaryIndices(srcRect, gray.width, gray.height, [
						dx,
						dy,
					])
					if (indices) {
						const diff = getAbsDiffForRectBounds(gray, indices, destIndices)
						if (diff < minDiff) {
							minDiff = diff
							minDx = dx
							minDy = dy
						}
					}
				}
			}
		}

		logAiInfo("redetect compare", {
			ratioDiff: minDiff / aveDiff,
			deltaDiff: aveDiff - minDiff,
			minDiff,
			aveDiff,
		})
		// If new region didn't improve the aveDiff a lot, then still use original source
		const ratio = minDiff / aveDiff
		if (
			(aveDiff - minDiff < 6 && ratio > 0.6) ||
			minDiff > 2 * boundDiffThresh ||
			(minDiff > boundDiffThresh && ratio > 0.5)
		) {
			continue
		}

		let deltaX = minDx / gray.width
		let deltaY = minDy / gray.height
		if (initialOrientation % 2) {
			const temp = deltaX
			deltaX = -deltaY
			deltaY = temp
		}

		logAiInfo("redetectSourceForHealing final", { minDx, minDy, minDiff, deltaX, deltaY })
		deltaX = clamp(
			deltaX,
			-healingItem.sourceRect.x,
			1 - healingItem.sourceRect.x - healingItem.sourceRect.width,
		)
		deltaY = clamp(
			deltaY,
			-healingItem.sourceRect.y,
			1 - healingItem.sourceRect.y - healingItem.sourceRect.height,
		)

		healingItem.sourceRect.x += deltaX
		healingItem.sourceRect.y += deltaY
		healingItem.sourceCenter[0] += deltaX
		healingItem.sourceCenter[1] += deltaY
	}

	return healingItems
}

function transferCrop(
	info: TransferInfo,
	flowResult: FlowResult | null | undefined,
): Partial<Adjustments> | null {
	const { imageInfo, keyImageInfo, keyImageCurrentAdjustments, dScene, dImageAspect } = info

	// no manual crop adjustment cases:
	if (!isCropAdjusted(keyImageCurrentAdjustments, keyImageInfo)) return null

	// ignore edge cases: we didn't transfer orientation and flip anyway
	if (
		keyImageCurrentAdjustments.orientation !== 0 ||
		keyImageCurrentAdjustments.flipX ||
		keyImageCurrentAdjustments.flipY
	) {
		return null
	}

	if (dScene > sceneDThreshForCrop || dImageAspect > 0.001) return null

	const feature = imageInfo.features
	const keyFeature = keyImageInfo.features
	if (feature && keyFeature) {
		// face count difference is too large
		if (
			absoluteDifference(feature.faceBoxCoords.length, keyFeature.faceBoxCoords.length) > 1 &&
			percentDifference(feature.faceBoxCoords.length, keyFeature.faceBoxCoords.length) >
				0.3 &&
			dScene > 0.11
		)
			return null

		const cropAdjustments = extractKeys(keyImageCurrentAdjustments, ["crop", "cropRatio"])
		const crop = cropAdjustments.crop

		// for getKeyAdjustmentsFast
		if (flowResult === undefined) return { ...cropAdjustments }

		// Transfer crop by overall image alignment
		if (flowResult) {
			// crop coordinates are based on assuming initialOrientation=0
			const [x, y, width, height] = crop
			const points: [vec2, vec2, vec2, vec2, vec2] = [
				[x, y],
				[x + width, y],
				[x + width, y + height],
				[x, y + height],
				[x + width / 2, y + height / 2],
			]
			const transPoints = points.map((val) =>
				transformInUncroppedSpace(val, {
					orientedSize: imageInfo.features.imageSize,
					initialOrientation: 0,
					flowResult,
				}),
			) as [vec2, vec2, vec2, vec2, vec2]
			const cx = getMean(transPoints.map((val) => val[0]))
			const cy = getMean(transPoints.map((val) => val[1]))
			const newCenter: vec2 = [cx, cy]
			const newW =
				(transPoints[1][0] - transPoints[0][0] + transPoints[2][0] - transPoints[3][0]) / 2
			const newH =
				(transPoints[2][1] - transPoints[1][1] + transPoints[3][1] - transPoints[0][1]) / 2

			const x1 = clamp(newCenter[0] - newW / 2, 0, 1)
			const x2 = clamp(newCenter[0] + newW / 2, 0, 1)
			const y1 = clamp(newCenter[1] - newH / 2, 0, 1)
			const y2 = clamp(newCenter[1] + newH / 2, 0, 1)
			let newCrop: Crop = [x1, y1, x2 - x1, y2 - y1]

			logAiInfo("transfer crop:", { crop, newCrop })

			newCrop = shrinkCropToAspectRatio(newCrop, width / height)

			cropAdjustments.crop = newCrop
			return cropAdjustments
		}

		// If no flow result, try to transfer crop with respect to all face regions (given same face count <=3)
		const newCrop = transferCropByFace(crop, feature, keyFeature)
		if (newCrop) {
			cropAdjustments.crop = newCrop
			return cropAdjustments
		}
	}

	return null
}

function shrinkCropToAspectRatio(crop: Crop, whRatio: number): Crop {
	const [x, y, width, height] = crop
	const currRatio = width / height

	if (equals(currRatio, whRatio)) return crop

	if (currRatio > whRatio) {
		const newWidth = height * whRatio
		const centerX = x + width / 2
		return [centerX - newWidth / 2, y, newWidth, height]
	} else {
		const newHeight = width / whRatio
		const centerY = y + height / 2
		return [x, centerY - newHeight / 2, width, newHeight]
	}
}

function transferCropByFace(crop: Crop, feature: ImageFeature, keyFeature: ImageFeature) {
	if (
		feature.faceBoxCoords.length > 0 &&
		feature.faceBoxCoords.length <= 3 &&
		feature.faceBoxCoords.length === keyFeature.faceBoxCoords.length
	) {
		// Adjust the crop according to face coords
		const allFaceBounds = getAllFaceBounds(feature.faceBoxCoords)
		const allFaceArea = getFaceArea(allFaceBounds)

		const allFaceBoundsKey = getAllFaceBounds(keyFeature.faceBoxCoords)
		const allFaceAreaKey = getFaceArea(allFaceBoundsKey)

		const allFaceCenter = getRectCenter(allFaceBounds)
		const allFaceCenterKey = getRectCenter(allFaceBoundsKey)
		const dx = allFaceCenter[0] - allFaceCenterKey[0]
		const dy = allFaceCenter[1] - allFaceCenterKey[1]

		const skinPercentDiff = percentDifference(
			feature.light.facePercent,
			keyFeature.light.facePercent,
			1,
		)
		const boxAreaDiff = percentDifference(allFaceArea, allFaceAreaKey)

		// Same face but different angle could make boxAreaDiff larger than expected
		const isLikelySameSingleFace =
			feature.faceBoxCoords.length === 1 &&
			Math.abs(dx) < 0.1 &&
			Math.abs(dy) < 0.1 &&
			boxAreaDiff < 0.6
		// logAiInfo("crop", { dx, dy, skinPercentDiff, boxAreaDiff, isLikelySameSingleFace })

		if (
			boxAreaDiff < 0.4 ||
			(skinPercentDiff < 0.3 && boxAreaDiff < 0.6) ||
			isLikelySameSingleFace
		) {
			const newCrop = crop.slice() as Crop
			newCrop[0] = clamp(crop[0] + dx, 0, 1 - crop[2])
			newCrop[1] = clamp(crop[1] + dy, 0, 1 - crop[3])

			if (!isRectCoveredByCrop(allFaceBounds, newCrop)) {
				return null
			}

			return newCrop
		}
	}

	return null
}

// Todo: transform straighten according to alignment result?
function transferStraighten(info: TransferInfo, autoStraightenSetting: boolean) {
	const { imageInfo, keyImageInfo, keyImageCurrentAdjustments, dScene } = info

	if (!isStraightenAdjusted(keyImageCurrentAdjustments, keyImageInfo, autoStraightenSetting)) {
		return null
	}

	const feature = imageInfo.features
	const keyFeature = keyImageInfo.features
	if (feature && keyFeature) {
		const currentStraighten = keyImageCurrentAdjustments.cropStraighten
		const autoStraighten = feature.autoCorrection.cropStraighten ?? 0
		const keyAutoStraighten = keyFeature.autoCorrection.cropStraighten ?? 0
		const diff = Math.abs(autoStraighten - keyAutoStraighten)
		const bias = currentStraighten - keyAutoStraighten
		// Case: user manually tapped auto, but autoStraighten is not turned on
		if (
			bias === 0 &&
			!autoStraightenSetting &&
			currentStraighten != 0 &&
			dScene < sceneDThreshForTransformLarge
		) {
			return autoStraighten
		}
		if (dScene < sceneDThreshForTransformSmall && diff <= 1) {
			return autoStraighten + bias
		}
	}

	return null
}

function transferPerspective(info: TransferInfo, autoPerspective: boolean) {
	const { imageInfo, keyImageInfo, keyImageCurrentAdjustments, dScene } = info

	const feature = imageInfo.features
	const keyFeature = keyImageInfo.features
	const defaultAdjustments = getDefaultAdjustments()
	if (feature && keyFeature) {
		const keyBiases = {}
		let totalAbsBias = 0
		let totalDiffFromDefault = 0
		for (const key of perspectiveKeys) {
			const autoValue = keyFeature.autoCorrection[key] ?? defaultAdjustments[key]
			const currValue = keyImageCurrentAdjustments[key]
			keyBiases[key] = currValue - autoValue
			totalAbsBias += Math.abs(keyBiases[key])
			totalDiffFromDefault += Math.abs(currValue - defaultAdjustments[key])
		}

		// no user changes in Transform for key photo
		if (
			(!autoPerspective && totalDiffFromDefault === 0) ||
			(autoPerspective && totalAbsBias === 0)
		) {
			return null
		}

		const keyAutoVertical = keyFeature.autoCorrection.perspectiveVertical ?? 0
		const autoVertical = feature.autoCorrection.perspectiveVertical ?? 0
		const autoVerticalDiff = Math.abs(keyAutoVertical - autoVertical)

		if (dScene < sceneDThreshForTransformSmall && autoVerticalDiff > 0.02) {
			// directly set the key photo's adjustments (auto might have error)
			const adjustments = extractKeys(keyImageCurrentAdjustments, perspectiveKeys)
			adjustments["cropStraighten"] = feature.autoCorrection.cropStraighten ?? 0

			return adjustments
		} else if (dScene < sceneDThreshForTransformLarge) {
			// use auto value + bias
			const adjustments = {}
			for (const key of perspectiveKeys) {
				const autoValue = feature.autoCorrection[key] ?? defaultAdjustments[key]
				adjustments[key] = autoValue + keyBiases[key]
			}
			// Also include auto straighten (do not add bias as that should be handled by transferStraighten)
			adjustments["cropStraighten"] = feature.autoCorrection.cropStraighten ?? 0

			return adjustments
		}
	}

	return null
}
